import { z } from "@hono/zod-openapi";
import { forecastLateV1 } from "../../appointment/forecastLate";
import { forecastOnTimeV1 } from "../../appointment/forecastOnTime";
import { callOutOfSequenceV1, callOutOfSequenceV2 } from "../../callRejection/callOutOfSequence";
import { dispatchLimitExceededV1, dispatchLimitExceededV2 } from "../../callRejection/dispatchLimitExceeded";
import { noAutoDispatchV1, noAutoDispatchV2 } from "../../callRejection/noAutoDispatch";
import { notAtStopV1, notAtStopV2 } from "../../callRejection/notAtStop";
import { otherRejectionV1, otherRejectionV2 } from "../../callRejection/otherRejection";
import { trailerMismatchV1, trailerMismatchV2 } from "../../callRejection/trailerMismatch";
import { arrivalV1 } from "../../movement/arrival";
import { departureV1 } from "../../movement/departure";
import { dvirV1 } from "../../inspection/dvir";
import { emptyCallV1, emptyCallV2 } from "../../call/emptyCall";
import { fuelStopV1 } from "../../fuel/fuelStop";
import { loadedCallV1, loadedCallV2 } from "../../call/loadedCall";
import { stopCallV1 } from "../../call/stopCall";
import type { TmsEventBodyOf, TmsEventKind } from "../../taxonomy";

/**
 * One outcome of a stop-workflow phase: a vendor task the driver completed, or a call the TMS refused.
 *
 * The stop workflow is a grid of stop roles against phases, so one pickup yields several of these at several instants
 * — an arrival, a call, a departure — rather than one event for the whole stop. A refusal is the same phase read from
 * the TMS's side: the driver called, and the TMS said why it would not take it. Not every vendor states every kind —
 * an in-cab platform reports completions, a TMS reports refusals — so a source's strategy states the subset it can.
 *
 * The kinds it may state are declared here once, and the registry holds the subject to them.
 * `satisfies` rather than an annotation, so each name stays a literal and a typo is still a compile error.
 */
export const stopEventKinds = [
  "emptyCall",
  "loadedCall",
  "stopCall",
  "fuelStop",
  "dvir",
  "arrival",
  "departure",
  "notAtStop",
  "trailerMismatch",
  "callOutOfSequence",
  "noAutoDispatch",
  "dispatchLimitExceeded",
  "otherRejection",
  "forecastLate",
  "forecastOnTime",
] as const satisfies readonly TmsEventKind[];

export type StopEventKind = (typeof stopEventKinds)[number];

/**
 * The type of `tms_event.stop_event.facts`: any of this subject's kinds, at any version it declares.
 *
 * What the generated Drizzle column's `.$type<…>()` names, and what the repository parses every written and read
 * value against — so the column's compile-time type and its runtime contents answer to the same schema.
 *
 * Written out one arm at a time rather than derived from the kind list above. Every version a column may hold
 * is named here, so shipping a second version of a kind is a visible edit in this file rather than a silent
 * widening, and forgetting it fails the first time the writer emits that version. When a kind does reach two,
 * its arms nest in a union on `version`: zod keeps only the first arm when a discriminator value repeats, so
 * listing both flat would quietly parse one version's documents against the other's schema.
 */
export const StopEventFactsSchema = z.discriminatedUnion("kind", [
  z.discriminatedUnion("version", [emptyCallV1.facts, emptyCallV2.facts]),
  z.discriminatedUnion("version", [loadedCallV1.facts, loadedCallV2.facts]),
  stopCallV1.facts,
  fuelStopV1.facts,
  dvirV1.facts,
  arrivalV1.facts,
  departureV1.facts,
  z.discriminatedUnion("version", [notAtStopV1.facts, notAtStopV2.facts]),
  z.discriminatedUnion("version", [trailerMismatchV1.facts, trailerMismatchV2.facts]),
  z.discriminatedUnion("version", [callOutOfSequenceV1.facts, callOutOfSequenceV2.facts]),
  z.discriminatedUnion("version", [noAutoDispatchV1.facts, noAutoDispatchV2.facts]),
  z.discriminatedUnion("version", [dispatchLimitExceededV1.facts, dispatchLimitExceededV2.facts]),
  z.discriminatedUnion("version", [otherRejectionV1.facts, otherRejectionV2.facts]),
  forecastLateV1.facts,
  forecastOnTimeV1.facts,
]);

export type StopEventFacts = z.infer<typeof StopEventFactsSchema>;

/**
 * What a StopEvent normalizer produces: references and facts, without the base the writer supplies.
 */
export type StopEventBody = TmsEventBodyOf<StopEventKind>;
